import { useMemo, useState } from 'react';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid2';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import { PageSection, StatTile } from '@radiology/ui';
import { Box1, CloseCircle, Cpu, Timer1 } from 'iconsax-react';
import { DeviceTable } from './DeviceTable';
import { DeviceDialog } from './DeviceDialog';
import { DeviceForm } from './DeviceForm';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import dayjs from 'dayjs';
import {
  useDeviceSummary,
  useDevices,
  useMarkNotificationRead,
  useNotifications,
  type Device,
} from './api';
import { rolesIn, useSession } from '../../auth/SessionContext';

/**
 * Devices screens.
 *
 * Unlike certifications and purchasing, every role here reads the same rows —
 * the register is not row-scoped (see docs/ACCESS_MATRIX.md § Devices for the
 * measured reason). What differs is what each role may DO, so the screens differ
 * by the actions they offer rather than by the data they show.
 */

function useCapabilities() {
  const session = useSession();
  const roles = rolesIn(session, 'devices');
  return {
    canEdit: roles.includes('admin') || roles.includes('secretary'),
    canDelete: roles.includes('admin'),
  };
}

function useTotals() {
  const { data } = useDeviceSummary();
  return useMemo(() => {
    const rows = data ?? [];
    const total = rows.reduce((n, r) => n + Number(r.total), 0);
    const obsolete = rows.reduce((n, r) => n + Number(r.obsolete), 0);
    const addedLastYear = rows.reduce((n, r) => n + Number(r.added_last_year), 0);
    const weighted = rows.reduce((n, r) => n + Number(r.average_age ?? 0) * Number(r.total), 0);
    return {
      rows,
      total,
      obsolete,
      inService: total - obsolete,
      addedLastYear,
      averageAge: total > 0 ? Math.round((weighted / total) * 10) / 10 : 0,
    };
  }, [data]);
}

/** QC and PM counts across the register, for the maintenance screen. */
function useDueCounts() {
  const { data } = useDevices('active');
  return useMemo(() => {
    const rows = data ?? [];
    const count = (field: 'qc_status' | 'pm_status', status: string) =>
      rows.filter(d => d[field] === status).length;
    return {
      qcOverdue: count('qc_status', 'Overdue'),
      qcDueSoon: count('qc_status', 'Due Soon'),
      pmOverdue: count('pm_status', 'Overdue'),
      pmDueSoon: count('pm_status', 'Due Soon'),
      unscheduled: rows.filter(
        d => d.qc_status === 'Not scheduled' || d.pm_status === 'Not scheduled',
      ).length,
    };
  }, [data]);
}

/**
 * In-system notifications (section 6.2).
 *
 * Kept on the maintenance screen rather than behind a bell in the shared shell:
 * the reminders are all about QC and PM, and this is where the secretary acts on
 * them.
 */
function NotificationList() {
  const { data } = useNotifications();
  const markRead = useMarkNotificationRead();
  const items = (data ?? []).filter(n => n.module === 'devices');

  if (items.length === 0) return null;

  return (
    <PageSection
      title="Reminders"
      description="QC and PM reminders sent 30 days before, 7 days before, and on the due date."
    >
      <Stack gap={1.25} divider={<Divider flexItem />}>
        {items.slice(0, 10).map(n => (
          <Stack
            key={n.id}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
          >
            <Stack>
              <Typography variant="body2" fontWeight={n.read_at ? 400 : 600}>
                {n.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {n.body} · {dayjs(n.created_at).format('D MMM YYYY')}
              </Typography>
            </Stack>
            {!n.read_at && (
              <Button size="small" onClick={() => markRead.mutate(n.id)}>
                Mark read
              </Button>
            )}
          </Stack>
        ))}
      </Stack>
    </PageSection>
  );
}

/** Devices at or past ten years — the fleet-renewal conversation. */
function useAgeing() {
  const { data } = useDevices('active');
  return useMemo(() => {
    const rows = data ?? [];
    return {
      ageing: rows.filter(d => (d.years_in_service ?? 0) >= 10).length,
      total: rows.length,
    };
  }, [data]);
}

// -----------------------------------------------------------------------------
// Secretary — maintains the register
// -----------------------------------------------------------------------------

export function AllDevices() {
  const [selected, setSelected] = useState<Device | null>(null);
  const { canEdit, canDelete } = useCapabilities();

  return (
    <Stack gap={2.5}>
      <PageSection
        title="Device register"
        description="Every device in the service. Exporting gives you what you can see, not the whole table."
        disablePadding
      >
        <DeviceTable
          emptyTitle="No devices"
          emptyDescription="Nothing has been added to the register yet."
          onAction={setSelected}
        />
      </PageSection>

      <DeviceDialog
        device={selected}
        canEdit={canEdit}
        canDelete={canDelete}
        onClose={() => setSelected(null)}
      />
    </Stack>
  );
}

export function NewDevice() {
  return (
    <Stack gap={2.5}>
      <DeviceForm />

      <PageSection
        title="Recently added"
        description="The ten most recent additions to the register."
        disablePadding
      >
        <DeviceTable emptyTitle="Nothing added yet" />
      </PageSection>
    </Stack>
  );
}

/**
 * Maintenance — the working queue.
 *
 * The legacy MaintenanceSchedule screen listed every device and let you set a
 * status against it. There are 0 maintenance rows in the migrated data, so
 * nothing was ever recorded through it. This surfaces what actually needs
 * attention instead: obsolete kit, and anything ten years or older.
 */
export function MaintenanceSchedule() {
  const [selected, setSelected] = useState<Device | null>(null);
  const { canEdit, canDelete } = useCapabilities();
  const t = useTotals();
  const age = useAgeing();
  const due = useDueCounts();

  return (
    <Stack gap={2.5}>
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="In service"
            value={t.inService}
            tone="success"
            icon={<Cpu size={20} color="currentColor" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="Obsolete"
            value={t.obsolete}
            tone="error"
            icon={<CloseCircle size={20} color="currentColor" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="Ten years or older"
            value={age.ageing}
            tone={age.ageing > 0 ? 'warning' : 'default'}
            hint="Still in service"
            icon={<Timer1 size={20} color="currentColor" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="Average age" value={`${t.averageAge} yrs`} />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="QC overdue"
            value={due.qcOverdue}
            tone={due.qcOverdue > 0 ? 'error' : 'success'}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="QC due soon"
            value={due.qcDueSoon}
            tone={due.qcDueSoon > 0 ? 'warning' : 'default'}
            hint="Within 30 days"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="PM overdue"
            value={due.pmOverdue}
            tone={due.pmOverdue > 0 ? 'error' : 'success'}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="PM due soon"
            value={due.pmDueSoon}
            tone={due.pmDueSoon > 0 ? 'warning' : 'default'}
            hint="Within 30 days"
          />
        </Grid>
      </Grid>

      {due.unscheduled > 0 && (
        <Alert severity="info">
          {due.unscheduled} {due.unscheduled === 1 ? 'device has' : 'devices have'} no QC or PM date
          yet, so nothing is scheduled and no reminder will be sent for them. Set a last date and
          the next one is calculated a year on.
        </Alert>
      )}

      <NotificationList />

      <Alert severity="info">
        Age, QC status and PM status are all recalculated on every read. The legacy app stored the
        age and refreshed it only when someone opened the edit form, so it drifted — 69 of the 154
        migrated devices carried a figure that was out by a year or more.
      </Alert>

      <PageSection
        title="QC and PM"
        description="Filter by status in the toolbar to see exactly what needs attention."
        disablePadding
      >
        <DeviceTable filter="active" emptyTitle="No devices in service" onAction={setSelected} />
      </PageSection>

      <PageSection
        title="Obsolete devices"
        description="Marked obsolete but still on the register. They are excluded from QC and PM reminders."
        disablePadding
      >
        <DeviceTable
          filter="obsolete"
          emptyTitle="Nothing is marked obsolete"
          onAction={setSelected}
        />
      </PageSection>

      <DeviceDialog
        device={selected}
        canEdit={canEdit}
        canDelete={canDelete}
        onClose={() => setSelected(null)}
      />
    </Stack>
  );
}

// -----------------------------------------------------------------------------
// Manager — read-only
// -----------------------------------------------------------------------------

/**
 * Manager dashboard.
 *
 * Replaces `getManagementInfo()`, which pulled every device into Node and
 * counted them in JavaScript on each request. These numbers come from
 * `devices.device_summary`, aggregated in the database.
 */
export function ManagerDashboard() {
  const [selected, setSelected] = useState<Device | null>(null);
  const t = useTotals();
  const age = useAgeing();

  const bySite = useMemo(() => {
    const map = new Map<string, { site: string; total: number; obsolete: number }>();
    for (const r of t.rows) {
      const entry = map.get(r.site) ?? { site: r.site, total: 0, obsolete: 0 };
      entry.total += Number(r.total);
      entry.obsolete += Number(r.obsolete);
      map.set(r.site, entry);
    }
    return [...map.values()].sort((a, b) => b.total - a.total);
  }, [t.rows]);

  const topSections = useMemo(
    () =>
      [...t.rows]
        .sort((a, b) => Number(b.total) - Number(a.total))
        .slice(0, 8)
        .map(r => ({
          label: `${r.site} · ${r.section}`,
          total: Number(r.total),
          age: Number(r.average_age ?? 0),
        })),
    [t.rows],
  );

  const max = topSections[0]?.total ?? 1;

  return (
    <Stack gap={2.5}>
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="Devices"
            value={t.total}
            icon={<Box1 size={20} color="currentColor" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="In service" value={t.inService} tone="success" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="Ten years or older"
            value={age.ageing}
            tone={age.ageing > 0 ? 'warning' : 'default'}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="Average age" value={`${t.averageAge} yrs`} />
        </Grid>
      </Grid>

      <PageSection title="By site" description="Where the fleet sits.">
        <Stack gap={1.25}>
          {bySite.map(row => (
            <Stack
              key={row.site}
              direction="row"
              justifyContent="space-between"
              alignItems="baseline"
              gap={2}
            >
              <Typography variant="body2">{row.site}</Typography>
              <Stack direction="row" gap={2} alignItems="baseline">
                <Typography variant="caption" color="text.secondary">
                  {row.obsolete} obsolete
                </Typography>
                <Typography variant="body2">{row.total}</Typography>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </PageSection>

      <PageSection
        title="Largest sections"
        description="Device count and average age, so ageing concentrations are visible."
      >
        <Stack gap={1.75}>
          {topSections.map(row => (
            <Stack key={row.label} gap={0.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography variant="body2">{row.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {row.total} · avg {row.age} yrs
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={(row.total / max) * 100}
                color={row.age >= 10 ? 'warning' : 'primary'}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Stack>
          ))}
        </Stack>
      </PageSection>

      <PageSection
        title="The register"
        description="Read-only — managers do not maintain the inventory."
        disablePadding
      >
        <DeviceTable emptyTitle="No devices" onAction={setSelected} />
      </PageSection>

      {/* canEdit/canDelete come from the session, so a manager who also holds
          secretary or admin keeps those actions here. */}
      <ManagerDialog device={selected} onClose={() => setSelected(null)} />
    </Stack>
  );
}

function ManagerDialog({ device, onClose }: { device: Device | null; onClose: () => void }) {
  const { canEdit, canDelete } = useCapabilities();
  return <DeviceDialog device={device} canEdit={canEdit} canDelete={canDelete} onClose={onClose} />;
}

// -----------------------------------------------------------------------------
// Admin
// -----------------------------------------------------------------------------

export function AdminDashboard() {
  const t = useTotals();
  const age = useAgeing();

  return (
    <Stack gap={2.5}>
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="Devices"
            value={t.total}
            icon={<Box1 size={20} color="currentColor" />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="In service" value={t.inService} tone="success" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile label="Obsolete" value={t.obsolete} tone="error" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            label="Ten years or older"
            value={age.ageing}
            tone={age.ageing > 0 ? 'warning' : 'default'}
          />
        </Grid>
      </Grid>

      <Alert severity="warning">
        Deleting a device also deletes its maintenance history, downtime record and trained-staff
        list. Only admins can do it, and marking a device obsolete is almost always the right action
        instead.
      </Alert>

      <PageSection title="Sites and sections" description="Every grouping in the register.">
        <Stack gap={1.25}>
          {t.rows
            .slice()
            .sort((a, b) => Number(b.total) - Number(a.total))
            .map(r => (
              <Stack
                key={`${r.site}·${r.section}`}
                direction="row"
                justifyContent="space-between"
                alignItems="baseline"
                gap={2}
              >
                <Typography variant="body2">
                  {r.site} · {r.section}
                </Typography>
                <Stack direction="row" gap={2} alignItems="baseline">
                  <Typography variant="caption" color="text.secondary">
                    avg {r.average_age ?? 0} yrs
                  </Typography>
                  <Typography variant="body2">{r.total}</Typography>
                </Stack>
              </Stack>
            ))}
        </Stack>
      </PageSection>
    </Stack>
  );
}
